import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import { clampToViewport, placeVertically, VerticalPlacement } from 'components/shared/viewport';
import { useAttachmentUrl } from 'hooks/useAttachmentUrl';
import { useClickAttachment } from 'hooks/useClickAttachment';
import { SummaryAttachment } from 'models/aiArtifacts/Summary';
import { useOpenCitationDocument } from 'stores/openCitationDocumentStore';
import { usePdfBytes } from 'stores/pdfBytesStore';
import colors from 'styles/theme/colors';

import type { CitationHighlight } from './citationHighlight';
import { formatProvenance } from './provenanceText';

const CitationPdfPreview = lazy(() => import('./CitationPdfPreview'));

export type CitationBadgeVariant = 'purple' | 'white';

type CitationBadgeProps = {
  attachment: SummaryAttachment;
  size?: number;
  // using a negative because most components will want to show the hover effect
  hideHoverEffect?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  attachmentType?: string;
  /**
   * Pages within the cited PDF to render in the hover preview, in citation order.
   * When undefined or empty, the badge falls back to a simple filename tooltip.
   */
  pages?: number[];
  /** Boxes on those pages to outline in the hover preview (see citationHighlight). */
  highlights?: CitationHighlight[];
  /**
   * Chip styling. Summary and comparison citations are purple; the referral clinical
   * assessment's are white-on-outline, matching the criteria card they sit in.
   */
  variant?: CitationBadgeVariant;
};

export default function CitationBadge({
  attachment,
  size = 16,
  hideHoverEffect = false,
  clickable = true,
  onClick,
  attachmentType,
  pages,
  highlights,
  variant = 'purple',
}: CitationBadgeProps) {
  const firstPage = pages?.[0];
  const openAttachment = useClickAttachment(attachment.attachmentId, attachmentType, firstPage);
  const handleClick = onClick ?? openAttachment;
  const interactive = clickable || !!onClick;
  // Native title still shown to mobile/tablet users where hover preview isn't reachable.
  const tooltipText = attachment.filename ?? undefined;

  return (
    <StyledCitationBadge
      size={size}
      hoverable={!hideHoverEffect}
      clickable={interactive}
      variant={variant}
      title={tooltipText}
      onClick={interactive ? handleClick : undefined}>
      {attachment.displayId}
      {!hideHoverEffect && (
        <PreviewLayer
          source={attachment}
          attachmentType={attachmentType}
          pages={pages}
          highlights={highlights}
          onActivate={interactive ? handleClick : undefined}
        />
      )}
    </StyledCitationBadge>
  );
}

type PreviewProps = {
  source: SummaryAttachment;
  attachmentType?: string;
  pages?: number[];
  highlights?: CitationHighlight[];
  /**
   * Opens the cited document. Given only when the badge is interactive — a badge that does
   * nothing (SourceChip's, whose surrounding chip owns the click) must not take keyboard focus
   * and then answer to nothing.
   */
  onActivate?: () => void;
};

// What a screen reader announces instead of a bare number. The visible chip is just its display
// id, which on its own tells a non-sighted reviewer nothing about what they are about to open.
function citationLabel(source: SummaryAttachment, pages?: number[]): string {
  const name = source.filename || `document ${source.displayId}`;
  if (!pages?.length) return `Open ${name}`;
  return pages.length === 1 ? `Open ${name} at page ${pages[0]}` : `Open ${name} at pages ${formatPageRanges(pages)}`;
}

function formatPageRanges(pages: number[]): string {
  const sorted = [...new Set(pages)].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i <= sorted.length; i++) {
    const current = sorted[i];
    if (current === prev + 1) {
      prev = current;
      continue;
    }
    ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = current;
    prev = current;
  }
  return ranges.join(', ');
}

const CARD_WIDTH = 380;
const CARD_PADDING = 12;
const SCROLL_AREA_WIDTH = CARD_WIDTH - CARD_PADDING * 2;
const MULTI_PAGE_WIDTH = Math.round(SCROLL_AREA_WIDTH * 0.8);
// Roughly how tall the card is once a one-page preview has rendered: a letter page at the scroll area's
// width, plus the caption, provenance line and padding. The card opens before the page has drawn, so
// placement has to go on this estimate rather than a measurement.
const PREVIEW_HEIGHT = Math.round(SCROLL_AREA_WIDTH * (11 / 8.5)) + 80;

function PreviewLayer({ source, attachmentType, pages, highlights, onActivate }: PreviewProps) {
  const isPdf = /\.pdf$/i.test(source.filename ?? '');
  const pdfPages = pages ?? [];
  const showPdf = isPdf && pdfPages.length > 0;
  const cachedBytes = usePdfBytes(source.attachmentId);
  const { url, fetch } = useAttachmentUrl(source.attachmentId, attachmentType);
  const provenanceLine = formatProvenance(source);
  const [visible, setVisible] = useState(false);
  // The viewer already shows this document, so a preview card would only cover part of it.
  const documentOpen = useOpenCitationDocument() === source.attachmentId;
  const [x, setX] = useState(0);
  const [placement, setPlacement] = useState<VerticalPlacement>({ side: 'below', top: 0, maxHeight: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const gap = 10;

  const cancelHide = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const scheduleHide = () => {
    cancelHide();
    hideTimerRef.current = setTimeout(() => setVisible(false), 120);
  };

  const handleEnter = () => {
    cancelHide();
    if (documentOpen) return;
    // The signed URL is only needed to fetch the PDF bytes; once those are
    // cached (keyed by attachment) no request — and no refetch that would
    // blank an open preview — is necessary.
    if (showPdf && !cachedBytes) fetch();
    if (!triggerRef.current) return;
    const parent = triggerRef.current.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    setX(clampToViewport(rect.left + rect.width / 2 - CARD_WIDTH / 2, CARD_WIDTH, window.innerWidth));
    // Below by default, flipped above a badge low on the screen — a card opened below one there landed
    // partly or wholly past the bottom edge.
    setPlacement(placeVertically(rect, PREVIEW_HEIGHT, window.innerHeight, gap));
    setVisible(true);
  };

  useEffect(() => () => cancelHide(), []);

  return (
    <>
      {/* The badge itself is a span, so the keyboard affordance lives here — this overlay already
          covers it exactly and already owns the show/hide state. Focus reveals the same card hover
          does, and Enter/Space opens the document, so a keyboard user gets the whole citation and
          not just the half a mouse reaches. Only when the badge actually does something:
          focusable-but-inert is worse than not focusable. */}
      <HoverArea
        ref={triggerRef}
        data-testid='citation-hover'
        role={onActivate ? 'button' : undefined}
        tabIndex={onActivate ? 0 : undefined}
        aria-label={onActivate ? citationLabel(source, pages) : undefined}
        onMouseEnter={handleEnter}
        onMouseLeave={scheduleHide}
        onFocus={handleEnter}
        onBlur={scheduleHide}
        onKeyDown={(event) => {
          if (!onActivate) return;
          // Space scrolls the page if it isn't swallowed; a real button swallows it.
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          onActivate();
        }}
      />
      {visible &&
        !documentOpen &&
        createPortal(
          <PreviewCard
            data-testid='citation-preview-card'
            style={{
              left: x,
              width: CARD_WIDTH,
              maxHeight: placement.maxHeight,
              ...(placement.side === 'below' ? { top: placement.top } : { bottom: placement.bottom }),
            }}
            onMouseEnter={cancelHide}
            onMouseLeave={scheduleHide}>
            <HoverBridge $gap={gap} $side={placement.side} />
            <Caption>
              {showPdf
                ? pdfPages.length === 1
                  ? `Page ${pdfPages[0]} of ${source.filename}`
                  : `Pages ${formatPageRanges(pdfPages)} of ${source.filename}`
                : source.filename}
            </Caption>
            {provenanceLine && <Provenance>{provenanceLine}</Provenance>}
            {showPdf && (cachedBytes || url) && (
              <Suspense fallback={<LoadingSpinner />}>
                <CitationPdfPreview
                  attachmentId={source.attachmentId}
                  url={url}
                  pages={pdfPages}
                  highlights={highlights}
                  width={pdfPages.length > 1 ? MULTI_PAGE_WIDTH : SCROLL_AREA_WIDTH}
                  filename={source.filename}
                />
              </Suspense>
            )}
            {showPdf && !cachedBytes && !url && <LoadingSpinner />}
          </PreviewCard>,
          document.body
        )}
    </>
  );
}

const purpleStyles = css`
  background: ${colors.ai20};
  color: ${colors.purpleExtraDark};
  border-radius: 26px;
`;

const whiteStyles = css`
  background: ${colors.white};
  color: ${colors.black100};
  border: 0.5px solid ${colors.black25};
  border-radius: 26px;

  &:hover {
    background: ${colors.blue5};
    border-color: ${colors.blue100};
  }
`;

export const StyledCitationBadge = styled.span<{
  size: number;
  hoverable: boolean;
  clickable: boolean;
  variant: CitationBadgeVariant;
}>`
  position: relative;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 8px;
  font-size: 8px;
  font-weight: 500;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  ${({ variant }) => (variant === 'white' ? whiteStyles : purpleStyles)}
  ${({ hoverable, variant }) =>
    hoverable &&
    variant === 'purple' &&
    css`
      &:hover {
        border: 0.5px solid ${colors.purpleExtraDark};
      }
    `}
`;

const HoverArea = styled.span`
  position: absolute;
  inset: 0;
  border-radius: inherit;

  // Keyboard focus needs to be visible on a 14px chip, so the ring sits outside it rather than
  // trying to fit within. Only on focus-visible, so a mouse click doesn't leave a ring behind.
  &:focus-visible {
    outline: 2px solid ${colors.blue100};
    outline-offset: 2px;
  }
`;

const PreviewCard = styled.div`
  position: fixed;
  z-index: 9999;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
`;

// Spans the gap between the badge and the card so the pointer can cross it without the card closing —
// above the card when it opens below the badge, below it when it opens above.
const HoverBridge = styled.span<{ $gap: number; $side: VerticalPlacement['side'] }>`
  position: absolute;
  left: 0;
  right: 0;
  height: ${({ $gap }) => $gap}px;
  ${({ $gap, $side }) => ($side === 'below' ? `top: -${$gap}px;` : `bottom: -${$gap}px;`)}
  background: transparent;
`;

function LoadingSpinner() {
  return (
    <LoadingWrapper>
      <CircularProgress color={colors.purple100} size={20} thickness={2} />
    </LoadingWrapper>
  );
}

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
`;

const Caption = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: ${colors.black100};
`;

const Provenance = styled.div`
  font-style: italic;
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
  color: ${colors.black50};
  margin-top: -4px;
`;
